// ═══════════════════════════════════════════════════════════════════════════════════
// [FAMLA-APP-COMPLETE] BRANDING CONTEXT - CONTEXTE WHITE LABELLING
// ═══════════════════════════════════════════════════════════════════════════════════
//
// [RÔLE] Contexte React pour gérer le branding personnalisé
// [OBJECTIF] Fournir le branding à toute l'application via variable d'environnement JSON
// [ARCHITECTURE] Provider + hooks - Configuration au build-time, zéro appel API
//
// ═══════════════════════════════════════════════════════════════════════════════════

import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { useTheme, Theme } from './ThemeContext';
import { tenantBranding, TenantBrandingDerived } from '../services/config';
import { injectBrandingCss, updateDocumentTitle, updateFavicon, removeBrandingCss } from '../utils/brandingCssInjector';
import Images from '../constants/Images';

interface BrandingContextType {
    branding: TenantBrandingDerived;
    isCustomBranding: boolean;
    getLogo: (theme?: Theme) => string;
    getExportLogo: () => string;
    getExportText: () => string;
}

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

export const useBranding = () => {
    const context = useContext(BrandingContext);
    if (context === undefined) {
        throw new Error('useBranding must be used within a BrandingProvider');
    }
    return context;
};

interface BrandingProviderProps {
    children: React.ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
    const { theme } = useTheme();

    useEffect(() => {
        console.log('[BrandingProvider] tenantBranding:', tenantBranding);
        console.log('[BrandingProvider] enabled:', tenantBranding.enabled);
        console.log('[BrandingProvider] primaryColor:', tenantBranding.primaryColor);
        
        if (tenantBranding.enabled) {
            console.log('[BrandingProvider] Injecting CSS with color:', tenantBranding.primaryColor);
            injectBrandingCss(tenantBranding);
            if (tenantBranding.appName !== 'Famla') {
                updateDocumentTitle(tenantBranding.appName);
            }
            if (tenantBranding.faviconUrl) {
                updateFavicon(tenantBranding.faviconUrl);
            }
        } else {
            console.log('[BrandingProvider] Branding disabled, removing CSS');
            removeBrandingCss();
        }
    }, []);

    const getLogo = useCallback((overrideTheme?: Theme): string => {
        const effectiveTheme = overrideTheme || theme;
        
        if (!tenantBranding.enabled) {
            return Images.logov1;
        }

        if (effectiveTheme === 'dark' && tenantBranding.logoDarkUrl) {
            return tenantBranding.logoDarkUrl;
        }
        if (effectiveTheme === 'light' && tenantBranding.logoLightUrl) {
            return tenantBranding.logoLightUrl;
        }
        
        return tenantBranding.logoUrl || Images.logov1;
    }, [theme]);

    const getExportLogo = useCallback((): string => {
        if (!tenantBranding.enabled) {
            return Images.logov1;
        }
        return tenantBranding.logoUrl || Images.logov1;
    }, []);

    const getExportText = useCallback((): string => {
        return tenantBranding.exportText;
    }, []);

    const value = useMemo(() => ({
        branding: tenantBranding,
        isCustomBranding: tenantBranding.enabled,
        getLogo,
        getExportLogo,
        getExportText
    }), [getLogo, getExportLogo, getExportText]);

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    );
};

export default BrandingContext;
